import { useCallback, useEffect, useRef, useState } from 'react';
import { apiUrl } from './api';

const dashboardCache = new Map();
const dashboardRequests = new Map();
const CACHE_TTL = 10 * 60_000;
const STATIC_DEMO = import.meta.env.VITE_ATLAS_STATIC_DEMO === '1';

async function fetchDashboard(host, force = false) {
  const cached = dashboardCache.get(host);
  if (!force && cached && Date.now() - cached.at < CACHE_TTL) return cached.data;
  if (!force && dashboardRequests.has(host)) return dashboardRequests.get(host);
  const request = fetch(apiUrl(`api/dashboard?host=${encodeURIComponent(host)}${force ? '&refresh=1' : ''}`), { cache: 'no-store' })
    .then(async (response) => {
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || `Dashboard request failed (${response.status})`);
      }
      const data = await response.json();
      dashboardCache.set(host, { data, at: Date.now() });
      return data;
    })
    .finally(() => dashboardRequests.delete(host));
  dashboardRequests.set(host, request);
  return request;
}

export function prefetchMemoryHosts(hosts) {
  const ready = hosts.filter((host) => host.id !== 'local' && host.state === 'ready' && !dashboardCache.has(host.id));
  const requests = ready.map((host, index) => new Promise((resolve) => setTimeout(resolve, 350 + index * 220)).then(() => fetchDashboard(host.id)).catch(() => null));
  Promise.allSettled(requests).then(() => fetchDashboard('all').catch(() => {}));
}

export function useMemoryData(host = 'local') {
  const [state, setState] = useState(() => ({ data: dashboardCache.get(host)?.data ?? null, loading: !dashboardCache.has(host), error: '', connected: STATIC_DEMO, changedPath: '' }));
  const refreshId = useRef(0);

  const refresh = useCallback(async (changedPath = '', force = false) => {
    const requestId = ++refreshId.current;
    setState((current) => ({ ...current, loading: current.data == null }));
    try {
      const data = await fetchDashboard(host, force);
      if (requestId !== refreshId.current) return;
      setState((current) => ({ ...current, data, loading: false, error: '', changedPath }));
    } catch (error) {
      if (requestId !== refreshId.current) return;
      setState((current) => ({ ...current, loading: false, error: error.message }));
    }
  }, [host]);

  useEffect(() => {
    const cached = dashboardCache.get(host)?.data ?? null;
    setState((current) => ({ ...current, data: cached, loading: !cached, error: '' }));
    refresh('', false);
  }, [host, refresh]);

  useEffect(() => {
    if (STATIC_DEMO) { setState((current) => ({ ...current, connected: true })); return undefined; }
    const events = new EventSource(apiUrl('api/events'));
    events.addEventListener('ready', () => setState((current) => ({ ...current, connected: true })));
    events.addEventListener('memory-change', (event) => {
      const payload = JSON.parse(event.data);
      if (host === 'local' || host === 'all' || payload.path === 'atlas-state' || payload.path === `remote:${host}`) {
        dashboardCache.delete(host);
        if (host === 'all') dashboardCache.delete('all');
        refresh(payload.path, host !== 'all');
      }
    });
    events.onerror = () => setState((current) => ({ ...current, connected: false }));
    return () => events.close();
  }, [host, refresh]);

  return { ...state, refresh };
}

export function useFleet() {
  const [state, setState] = useState({ hosts: [], atlasState: { hidden: [], forgotten: [] }, loading: true, error: '' });
  const refresh = useCallback(async (force = false) => {
    try {
      const response = await fetch(apiUrl(`api/fleet${force ? '?refresh=1' : ''}`), { cache: 'no-store' });
      if (!response.ok) throw new Error(`Fleet request failed (${response.status})`);
      const payload = await response.json();
      setState({ hosts: payload.hosts, atlasState: payload.state || { hidden: [], forgotten: [] }, loading: false, error: '' });
    } catch (error) {
      setState((current) => ({ ...current, loading: false, error: error.message }));
    }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  return { ...state, refresh };
}

export function useDebouncedValue(value, delay = 180) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}
